import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheck, FiX, FiArrowRight, FiZap, FiUsers, FiTrendingUp } = FiIcons;

function PricingPage() {
  const [isAnnual, setIsAnnual] = useState(false);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const stagger = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const plans = [
    {
      name: 'Starter',
      description: 'Perfect for individual designers and developers',
      monthlyPrice: 0,
      annualPrice: 0,
      icon: FiZap,
      color: 'purple',
      popular: false,
      features: [
        '5 clones per month',
        'Basic HTML/CSS export',
        'Standard support',
        'Personal use only',
        '1 user account'
      ],
      limitations: [
        'No Webflow export',
        'No team collaboration',
        'Limited customization'
      ]
    },
    {
      name: 'Professional',
      description: 'Best for freelancers and small agencies',
      monthlyPrice: 29,
      annualPrice: 290,
      icon: FiUsers,
      color: 'blue',
      popular: true,
      features: [
        '50 clones per month',
        'All export formats (Webflow, Framer, HTML/CSS)',
        'Advanced editor with custom CSS',
        'Priority support',
        'Commercial license',
        'Up to 3 team members',
        'Clone library & organization',
        'Custom branding removal'
      ],
      limitations: []
    },
    {
      name: 'Enterprise',
      description: 'For large teams and agencies',
      monthlyPrice: 99,
      annualPrice: 990,
      icon: FiTrendingUp,
      color: 'green',
      popular: false,
      features: [
        'Unlimited clones',
        'All export formats',
        'Advanced editor + AI assistance',
        'White-label solution',
        'Dedicated account manager',
        'Unlimited team members',
        'Advanced analytics',
        'API access',
        'Custom integrations',
        'SLA guarantee'
      ],
      limitations: []
    }
  ];

  const getPrice = (plan) => {
    return isAnnual ? plan.annualPrice : plan.monthlyPrice;
  };

  const getSavings = (plan) => {
    if (plan.monthlyPrice === 0) return 0;
    return (plan.monthlyPrice * 12) - plan.annualPrice;
  };

  return (
    <div className="pt-16">
      {/* Header Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-800 mb-6">
              Simple, transparent pricing
            </h1>
            <p className="text-xl text-slate-600 mb-8">
              Choose the perfect plan for your cloning needs. No hidden fees, cancel anytime.
            </p>

            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-4 mb-12">
              <span className={`font-medium ${!isAnnual ? 'text-slate-800' : 'text-slate-500'}`}>
                Monthly
              </span>
              <button
                onClick={() => setIsAnnual(!isAnnual)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  isAnnual ? 'bg-purple-600' : 'bg-slate-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isAnnual ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`font-medium ${isAnnual ? 'text-slate-800' : 'text-slate-500'}`}>
                Annual
              </span>
              {isAnnual && (
                <span className="bg-green-100 text-green-800 text-sm font-medium px-2 py-1 rounded-full">
                  Save up to 17%
                </span>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="grid lg:grid-cols-3 gap-8"
            initial="initial"
            animate="animate"
            variants={stagger}
          >
            {plans.map((plan, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className={`relative bg-white rounded-2xl shadow-lg border-2 transition-all hover:shadow-xl ${
                  plan.popular 
                    ? 'border-purple-200 ring-2 ring-purple-100' 
                    : 'border-slate-100 hover:border-slate-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="p-8">
                  {/* Plan Header */}
                  <div className="text-center mb-8">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
                      plan.color === 'purple' ? 'bg-purple-100' :
                      plan.color === 'blue' ? 'bg-blue-100' : 'bg-green-100'
                    }`}>
                      <SafeIcon 
                        icon={plan.icon} 
                        className={`w-8 h-8 ${
                          plan.color === 'purple' ? 'text-purple-600' :
                          plan.color === 'blue' ? 'text-blue-600' : 'text-green-600'
                        }`} 
                      />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-800 mb-2">{plan.name}</h3>
                    <p className="text-slate-600">{plan.description}</p>
                  </div>

                  {/* Pricing */}
                  <div className="text-center mb-8">
                    <div className="flex items-baseline justify-center gap-2">
                      <span className="text-4xl font-bold text-slate-800">
                        ${getPrice(plan)}
                      </span>
                      <span className="text-slate-600">
                        /{isAnnual ? 'year' : 'month'}
                      </span>
                    </div>
                    {isAnnual && plan.monthlyPrice > 0 && (
                      <p className="text-sm text-green-600 mt-2">
                        Save ${getSavings(plan)} per year
                      </p>
                    )}
                  </div>

                  {/* Features */}
                  <div className="space-y-4 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start gap-3">
                        <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{feature}</span>
                      </div>
                    ))}
                    {plan.limitations.map((limitation, limitIndex) => (
                      <div key={limitIndex} className="flex items-start gap-3">
                        <SafeIcon icon={FiX} className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-500">{limitation}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <button 
                    className={`w-full py-4 px-6 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                      plan.popular
                        ? 'bg-purple-600 text-white hover:bg-purple-700 transform hover:scale-105'
                        : 'bg-slate-100 text-slate-800 hover:bg-slate-200'
                    }`}
                  >
                    {plan.name === 'Starter' ? 'Get Started Free' : `Start ${plan.name} Plan`}
                    <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-slate-800 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-slate-600">
              Everything you need to know about ClonewebX pricing
            </p>
          </motion.div>

          <motion.div 
            className="space-y-6"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            {[
              {
                question: "Can I change my plan at any time?",
                answer: "Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate any charges."
              },
              {
                question: "What happens if I exceed my clone limit?",
                answer: "If you exceed your monthly clone limit, you'll be prompted to upgrade your plan or wait until the next billing cycle. We'll never charge you without permission."
              },
              {
                question: "Do you offer refunds?",
                answer: "Yes, we offer a 30-day money-back guarantee for all paid plans. If you're not satisfied, contact us for a full refund."
              },
              {
                question: "Can I use cloned websites commercially?",
                answer: "Commercial use is included with Professional and Enterprise plans. The Starter plan is for personal use only."
              },
              {
                question: "Is there a free trial?",
                answer: "Our Starter plan is completely free and includes 5 clones per month. You can also try any paid plan free for 14 days."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white rounded-lg p-6 shadow-sm"
              >
                <h3 className="text-lg font-semibold text-slate-800 mb-2">
                  {faq.question}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {faq.answer}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-slate-800 mb-4">
              Ready to start cloning?
            </h2>
            <p className="text-xl text-slate-600 mb-8">
              Join thousands of designers and developers who are already using ClonewebX
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-purple-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-purple-700 transition-all transform hover:scale-105 flex items-center justify-center gap-2">
                Start Free Trial
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </button>
              <button className="border-2 border-slate-300 text-slate-700 px-8 py-4 rounded-lg font-semibold text-lg hover:border-slate-400 transition-all">
                Contact Sales
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default PricingPage;